import type React from "react";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Legend as CustomizedLegend } from "./Legend";

import moment from "moment";
import { useTranslation } from "react-i18next";
import type { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import type { AxisDomainItem } from "recharts/types/util/types";
import { getAlpha } from "senf-shared/utils/helpers";
import { v4 as uuidv4 } from "uuid";
import { useMeasure } from "../../../hooks/useMeasure";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import Dropdown from "../dropdown/Dropdown";
import { ChartTooltip } from "./ChartTooltip";
import type { ChartsProps } from "./chart.types";
import { DAY, getWeek, xAxisTickFormatter } from "./helpers";

const useDragToZoom = ({ state, setState, zoomTo, active }) => {
  const onMouseDown: CategoricalChartFunc = (e) =>
    active &&
    setState((s) => ({
      ...s,
      refAreaLeft: e?.activeLabel || "",
    }));
  const onMouseMove: CategoricalChartFunc = (e) =>
    active &&
    state.refAreaLeft &&
    setState((s) => ({
      ...s,
      refAreaRight: e?.activeLabel || "",
    }));
  const onMouseUp: CategoricalChartFunc = () =>
    active &&
    zoomTo([state.refAreaLeft, state.refAreaRight].sort((a, b) => a - b));
  return { onMouseDown, onMouseMove, onMouseUp };
};

const getAxisYDomain = <T extends Record<string, number>>(
  data: T[],
  from: AxisDomainItem,
  to: AxisDomainItem,
  dataKeys: ChartsProps["dataKeys"],
) => {
  const start =
    typeof from === "number" ? data.findIndex((e) => e.date > from) - 1 : 1;
  const end =
    typeof to === "number" ? data.findIndex((e) => e.date > to) : data.length;
  const refData = data.slice(Math.max(start, 0), end);
  let [bottom, top] = [0, 0];
  refData.forEach((d) => {
    Object.keys(dataKeys).forEach((key) => {
      if (d[key] >= top) top = d?.[key];
      if (d[key] <= bottom) bottom = d?.[key];
    });
  });

  return [0, (top + 1) * 1.1] as [AxisDomainItem, AxisDomainItem];
};

interface InitialStateShape {
  data: ChartsProps["data"];
  refAreaLeft: number | null;
  refAreaRight: number | null;
  animation: boolean;
}

// Helper function to get the start of the week (Monday) for a given date
function getStartOfWeek(date: Date) {
  const day = moment(date).day();
  const startOfWeek = moment(date)
    .subtract(day === 0 ? 7 : day, "days")
    .startOf("day")
    .add(1, "weeks");

  return startOfWeek.toDate();
}
const reducedArray = (data) =>
  Object.values(
    data.reduce((result, obj) => {
      const weekStartDate = getStartOfWeek(new Date(obj.date));
      const weekKey = weekStartDate.getTime();
      const newResult = { ...result };

      if (!newResult[weekKey]) newResult[weekKey] = { date: weekKey };

      Object.keys(obj).forEach((key) => {
        if (key !== "date") {
          newResult[weekKey][key] = (newResult[weekKey][key] || 0) + obj[key];
        }
      });

      return newResult;
    }, {}),
  );

const process = (data, view) => {
  const reduced = view === "weeks" ? reducedArray(data) : data;

  return reduced.map(({ date, ...d }) => ({
    ...d,
    date: Math.floor(date / DAY),
  }));
};

const belowLimit = (left: AxisDomainItem, date: number) =>
  typeof left !== "number" || left < date;

interface ZoomButtonsProps {
  state: InitialStateShape;
  domain: [AxisDomainItem, AxisDomainItem];
  zoom: number | null;
  setZoom: (e: number | null) => void;
  zoomTo: (e: [AxisDomainItem, AxisDomainItem]) => void;
  view: "weeks" | "days";
  setView: React.Dispatch<React.SetStateAction<"weeks" | "days">>;
  className?: string;
}
const ZoomButtons = ({
  state,
  domain,
  zoom,
  setZoom,
  zoomTo,
  view,
  setView,
  className,
}: ZoomButtonsProps) => {
  const { t } = useTranslation();
  const left = state.data[0]?.date;
  const right = state.data[state.data.length - 1]?.date;
  const options = {
    weeks: t("global:weeks"),
    days: t("global:days"),
  };
  const zoomLevels = [
    // {
    //   days: 1,
    //   text: "Tag",
    // },
    {
      days: 7,
      text: t("global:week"),
    },
    {
      days: 30,
      text: t("global:month"),
    },
    {
      days: 90,
      text: t("global:quarter"),
    },
    {
      days: 180,
      text: t("global:halfYear"),
    },
    {
      days: 365,
      text: t("global:year"),
    },
    {
      days: Number.POSITIVE_INFINITY,
      text: t("global:wholePeriod"),
    },
  ];

  const [startDomain, endDomain] = domain.map((e, i) =>
    typeof e === "number" ? e : i === 0 ? left : right,
  );
  const disabledPrev = startDomain <= left;
  const disabledNext = endDomain >= right;
  const weekStart = Math.floor(
    moment(startDomain * DAY)
      .startOf("isoWeek")
      .add(1, "days")
      .unix() /
      (DAY / 1000),
  );

  const matchedStart = zoom === 7 && view === "days" ? weekStart : startDomain;
  const matchedEnd =
    zoom === 7 && view === "days" ? matchedStart + 6 : endDomain;

  const handlePrev = () => {
    const diff = Math.max(0, Math.min(zoom || 0, matchedStart - left));
    const d = [matchedStart - diff, matchedEnd - diff] as [
      AxisDomainItem,
      AxisDomainItem,
    ];
    zoomTo(d);
  };
  const handleNext = () => {
    const diff = Math.max(0, Math.min(zoom || 0, right - matchedEnd));
    const d = [matchedStart + diff, matchedEnd + diff] as [
      AxisDomainItem,
      AxisDomainItem,
    ];
    zoomTo(d);
  };
  return (
    <div className={cn("-mx-3 flex justify-between", className)}>
      <div className="flex">
        {zoomLevels &&
          zoomLevels
            .map((e, i) => {
              // find item in state.data where right - days is closest to date
              const start = state.data.reduce((acc, curr) => {
                return curr.date < right - e.days ? curr.date : acc;
              }, 0);

              if (i !== zoomLevels.length - 1 && belowLimit(start, left))
                return null;
              return (
                <Button
                  key={e.days}
                  onClick={() => {
                    if (e.days === Number.POSITIVE_INFINITY) {
                      zoomTo(["dataMin", "dataMax"]);
                      setZoom(null);
                      return;
                    }
                    setZoom(e.days);
                    zoomTo([start, right]);
                  }}
                  text={e.text}
                  variant="shapeless"
                  className={cn(
                    "text-text-subdued hover:text-text-default hover:bg-transparent active:bg-transparent",
                    (zoom === e.days ||
                      (!zoom && i === zoomLevels.length - 1)) &&
                      "text-text-default",
                  )}
                />
              );
            })
            .filter(Boolean)}
      </div>
      <div className="flex">
        <Dropdown
          leadingIcon="eye"
          variant="shapeless"
          className={cn(
            "hover:text-text-default hover:bg-transparent active:bg-transparent",
          )}
          items={Object.entries(options).map(([key, label]) => ({
            text: label,
            onClick: () => setView(key as "weeks" | "days"),
          }))}
          // text={view}
          text={options[view]}
        />
        <div className="flex items-center">
          <Button
            onClick={handlePrev}
            disabled={disabledPrev}
            disabledNotice="Hier geht's nicht weiter!"
            leadingIcon="arrowhead-left-fill"
            variant="shapeless"
            className={cn(
              "text-text-subdued hover:text-text-default hover:bg-transparent active:bg-transparent",
              disabledPrev && "opacity-50",
            )}
          />
          <span className="whitespace-nowrap">
            {zoom === 7 && endDomain < right ? getWeek(startDomain * DAY) : ""}
          </span>
          <Button
            onClick={handleNext}
            disabled={disabledNext}
            disabledNotice="Hier geht's nicht weiter!"
            leadingIcon="arrowhead-right-fill"
            variant="shapeless"
            className={cn(
              "text-text-subdued hover:text-text-default hover:bg-transparent active:bg-transparent",
              disabledNext && "opacity-50",
            )}
          />
        </div>
      </div>
    </div>
  );
};

const DateChart = forwardRef<HTMLDivElement, ChartsProps>(
  (
    {
      data,
      dataKeys,
      className,
      legend,
      legendClassName,
      checkboxState,
      type = "line",
      dragZoom = false,
      lineType = "monotone",
      showHeader = true,
      height = "100%",
    },
    ref,
  ) => {
    const id = useRef(uuidv4());
    const { i18n } = useTranslation();
    const [zoom, setZoom] = useState<number | null>(null);
    const [domain, setDomain] = useState<{
      x: [AxisDomainItem, AxisDomainItem];
      y: [AxisDomainItem, AxisDomainItem];
    }>({
      x: ["dataMin", "dataMax"],
      y: ["dataMin", "dataMax"],
    });

    const [view, setView] = useState<"weeks" | "days">(
      data.length > 30 ? "weeks" : "days",
    );
    const [state, setState] = useState<InitialStateShape>({
      data: process(data, view),
      refAreaLeft: null,
      refAreaRight: null,
      animation: true,
    });
    // const [{ ref: outerRef }, { width }] = useMeasure<HTMLDivElement>();

    const zoomTo = ([left, right]: [AxisDomainItem, AxisDomainItem]) => {
      setState((e) => ({
        ...e,
        data: process(data, view),
        refAreaLeft: null,
        refAreaRight: null,
      }));
      const cancel = left === right || right === "";
      const min = belowLimit(left, state.data[0].date) ? "dataMin" : left;
      setDomain(({ x }) => {
        const [l, r] = x;
        return {
          x: [cancel ? l : min, cancel ? r : right],
          y: getAxisYDomain(
            state.data,
            cancel ? l : min,
            cancel ? r : right,
            dataKeys,
          ),
        };
      });
    };

    useEffect(() => {
      setState((e) => ({
        ...e,
        data: process(data, view),
      }));
      // setDomain(({ x }) => {
      //   const [l] = x;
      //   return {
      //     x: [l, "dataMax"],
      //     y: getAxisYDomain(state.data, l, "dataMax", dataKeys),
      //   };
      // });
    }, [data, view]);

    const { onMouseDown, onMouseMove, onMouseUp } = useDragToZoom({
      state,
      setState,
      zoomTo,
      active: dragZoom,
    });

    const ChartType = useMemo(
      () =>
        ({
          line: {
            Wrapper: LineChart,
            Plot: Line,
            props: (value: ChartsProps["dataKeys"][string]) => ({
              type: lineType,
              stroke: value.color,
              // connectNulls: true,
            }),
          },
          bar: {
            Wrapper: BarChart,
            Plot: Bar,
            props: (value: ChartsProps["dataKeys"][string]) => ({
              fill: value.color,
              radius: [3, 3, 0, 0],
            }),
          },
        })[type || "line"],
      [type],
    );

    const legendPayload = legend
      ? Object.entries(dataKeys)
          ?.filter(([key]) => !checkboxState || (key && checkboxState[key]))
          ?.map(([_, item]) => ({
            ...item,
            payload: item,
          }))
      : [];

    return (
      <div
        className={cn(
          "flex h-full w-full select-none flex-col gap-5",
          className,
        )}
        // ref={outerRef}
      >
        {showHeader && (
          <ZoomButtons
            state={state}
            zoom={zoom}
            domain={domain.x}
            setZoom={setZoom}
            zoomTo={zoomTo}
            view={view}
            setView={setView}
          />
        )}
        <div
          ref={ref}
          className={cn("h-full w-full select-none")}
          // [&_.recharts-cartesian-grid-horizontal_line:nth-last-child(2)]:opacity-0
        >
          <ResponsiveContainer width="100%" height={height || "100%"}>
            <ChartType.Wrapper
              data={state.data}
              margin={{
                right: 3,
                top: 3,
              }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
            >
              <CartesianGrid
                className="stroke-border-subdued opacity-50"
                vertical={false}
                // fill="red"
              />
              <XAxis
                key={id.current}
                allowDataOverflow
                dataKey="date"
                domain={domain.x}
                padding={type === "bar" ? "no-gap" : undefined}
                // this is a hack to show bar charts if they have only one data point
                type={state.data?.length > 1 ? "number" : undefined}
                scale={state.data?.length > 1 ? "time" : undefined}
                tickLine={{ stroke: "transparent" }}
                // tickLine={{
                //   transform: `translate(0,-1)`,
                //   stroke: "var(--border-subdued)",
                // }}
                axisLine={{ stroke: "white" }}
                tickFormatter={(e) =>
                  xAxisTickFormatter({
                    value: e,
                    view,
                    locale: i18n.resolvedLanguage,
                    domain: domain.x,
                  })
                }
                tickMargin={5}
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                }}
              />
              <YAxis
                allowDataOverflow
                domain={domain.y}
                type="number"
                scale="time"
                yAxisId="1"
                tickLine={{ stroke: "transparent" }}
                axisLine={{ stroke: "transparent" }}
                tickFormatter={(e) => {
                  if (e >= 0) return e.getTime();
                  return "";
                }}
                width={40}
                tickMargin={5}
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                }}
                padding={{ bottom: 1, top: 0 }}
              />
              <Tooltip
                content={<ChartTooltip view={view} />}
                allowEscapeViewBox={{
                  // x: true,
                  y: true,
                }}
                cursor={{ fill: "var(--palette-neutral-200)" }}
                payload={data}
              />
              {Object.entries(dataKeys)?.map(([key, value], index) => (
                <ChartType.Plot
                  yAxisId="1"
                  key={index}
                  dataKey={key}
                  strokeWidth={2}
                  dot={false}
                  animationDuration={300}
                  {...ChartType.props(value)}
                />
              ))}
              {state.refAreaLeft && state.refAreaRight ? (
                <ReferenceArea
                  yAxisId="1"
                  x1={state.refAreaLeft}
                  x2={state.refAreaRight}
                  strokeOpacity={0.3}
                  style={{
                    fill: "var(--surface-highlight-subdued)",
                  }}
                />
              ) : null}
            </ChartType.Wrapper>
          </ResponsiveContainer>
          {legend && legendPayload && (
            <div className={cn("m-5 mt-0", legendClassName)}>
              <CustomizedLegend payload={legendPayload} type={type} />
            </div>
          )}
        </div>
      </div>
    );
  },
);

DateChart.displayName = "DateChart";
export default DateChart;
